import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { setSelectedView, getFilteredAssets, setSearchByAssetType } from '@/store/slices/assetsPreviewSlice';
import { setSearchValue } from '@/store/slices/applicationSlice';
import { RootState } from '@/store/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  ASSETS_PREVIEW_INNER_TABS,
  ASSETS_TYPE,
  FILTER_PARAMS,
  PORTAL_TARGETS,
  ASSET_STUDIO,
} from '@/types/shared/navigation-tabs.enums';
import { useFetchAssets, useAssetsData, useViewportWidth, useAppFilters, usePortal, useQueryNavigation } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { GridView, TableView } from '../AssetPreviewViews';
import { SectionEmptyState } from '../EmptyStates';
import { Asset } from '@/types/models/assets.types';
import { useSearchParams, useParams, generatePath } from 'react-router-dom';
import BaseInput from '@/components/uiKit/Inputs/BaseInput';
import { Icon } from '@/components/shared/display/Icon';
import { CloseIcon, SearchIcon } from '@/assets/Icons';
import { locations } from '@/locations';
import { useAssetsPreviewTable } from '@/hooks/AssetsPreviewPage/AssetsOverview/useAssetsPreviewTable';

export const Overview = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedView, currentFiltersValues } = useSelector((state: RootState) => state.assetsPreview);
  const { isMobile } = useViewportWidth();
  const { isLoading, assets } = useFetchAssets();
  const { assetsType } = useAppFilters();
  const [searchParams] = useSearchParams();
  const selectedTab = searchParams.get(FILTER_PARAMS.ASSETS_SECTION);
  const { navigateWithQuery } = useQueryNavigation();
  const { siteId } = useParams();
  const { tableStructure } = useAssetsPreviewTable();

  const { required, notRequired } = useAssetsData(assets);

  const onMachineItemClick = (data: Asset) => {
    navigateWithQuery(generatePath(locations.protected.sites.components, { siteId, machineId: data.asset_id }), {
      addParams: { [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS },
    });
  };

  const [requireAttention, notRequireAttention] = useMemo(() => {
    const applyFilter = (data: any) =>
      assetsType === ASSETS_TYPE.MOBILE
        ? currentFiltersValues
          ? getFilteredAssets(data?.mobile || [], currentFiltersValues, assetsType)
          : null
        : currentFiltersValues
        ? getFilteredAssets(data?.fixed || [], currentFiltersValues, assetsType)
        : null;

    return [applyFilter(required), applyFilter(notRequired)];
  }, [assetsType, currentFiltersValues, required, notRequired, assets]);

  const tableAssets = [];
  if (requireAttention?.length) {
    tableAssets.push(...requireAttention);
  }
  if (notRequireAttention?.length) {
    tableAssets.push(...notRequireAttention);
  }

  useEffect(() => {
    if (!selectedView) {
      dispatch(setSelectedView('grid'));
    }
  }, [isMobile, selectedView, dispatch]);

  if (selectedTab === ASSETS_PREVIEW_INNER_TABS.EXPERTS)
    return <ExpertViewWrapper enableSearch renderSearchInto={PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER} />;

  return (
    <div className="flex-col gap-4 h-full flex">
      {isLoading ? (
        <>
          <Skeleton className="h-[calc(50vh-90px)]" />
          <Skeleton className="h-[calc(50vh-90px)]" />
        </>
      ) : (
        <div className="mt-2">
          {selectedView === 'grid' ? (
            <GridViewWrapper
              requireAttention={requireAttention}
              notRequireAttention={notRequireAttention}
              enableSearch
              renderSearchInto={PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER}
            />
          ) : (
            <div>
              <TableView<Asset>
                emptyStateMessage={{
                  message: (
                    <>
                      <div>No assets found.</div>
                      <div>Try adjusting your filters or search terms.</div>
                    </>
                  ),
                }}
                items={tableAssets}
                columns={tableStructure}
                rowClickHandler={onMachineItemClick}
                renderSearchInto={PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
interface GridSection {
  assets: Asset[];
  isRequiredAttention: boolean;
  emptyText: string;
}

interface GridViewWrapperProps {
  requireAttention?: Asset[] | null;
  notRequireAttention?: Asset[] | null;
  enableSearch?: boolean;
  renderSearchInto?: PORTAL_TARGETS;
}

export function GridViewWrapper({
  requireAttention,
  notRequireAttention,
  enableSearch,
  renderSearchInto,
}: GridViewWrapperProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { assetsType } = useAppFilters();
  const searchAsset = useSelector((state: RootState) => state.assetsPreview.searchByAssetType?.[assetsType]);

  const { render } = usePortal(renderSearchInto);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const gridSections: GridSection[] = [
    {
      assets: requireAttention ?? [],
      isRequiredAttention: true,
      emptyText: t('assetsOverview.emptyState.requireAttention'),
    },
    {
      assets: notRequireAttention ?? [],
      isRequiredAttention: false,
      emptyText: t('assetsOverview.emptyState.dontRequireAttention'),
    },
  ];

  const filterAssets = (assets: Asset[]) => {
    if (!searchAsset) return assets;
    return assets.filter((asset) => asset.asset_ui_name.toLowerCase().includes(searchAsset.toLowerCase()));
  };

  const filteredSections = useMemo(
    () =>
      gridSections.map((section) => ({
        ...section,
        assets: filterAssets(section.assets),
      })),
    [gridSections, searchAsset]
  );

  const handleClearInput = () => {
    dispatch(setSearchByAssetType({ assetType: assetsType, search: '' }));
    dispatch(setSearchValue(''));
    searchInputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchByAssetType({ assetType: assetsType, search: e.target.value }));
    dispatch(setSearchValue(e.target.value));
  };

  const searchElement = (
    <BaseInput
      ref={searchInputRef}
      placeholder="Search asset name..."
      value={searchAsset}
      onChange={handleInputChange}
      aria-label="Search input"
      type="text"
      variant="buttonWrapper"
      rightButton={{
        element: searchAsset?.length > 0 ? <Icon icon={CloseIcon} /> : <Icon icon={SearchIcon} />,
        onClick: handleClearInput,
      }}
    />
  );

  return (
    <>
      {enableSearch && (renderSearchInto ? render(searchElement) : searchElement)}

      {filteredSections.map(({ assets, isRequiredAttention, emptyText }) =>
        assets.length ? (
          <GridView key={isRequiredAttention.toString()} assets={assets} isRequiredAttention={isRequiredAttention} />
        ) : (
          <SectionEmptyState key={isRequiredAttention.toString()} text={emptyText} />
        )
      )}
    </>
  );
}

//temporary
export function ExpertViewWrapper({
  enableSearch,
  renderSearchInto,
}: {
  enableSearch?: boolean;
  renderSearchInto?: PORTAL_TARGETS;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { assetsType } = useAppFilters();
  const searchAsset = useSelector((state: RootState) => state.assetsPreview.searchByAssetType?.[assetsType]);

  const { render } = usePortal(renderSearchInto);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClearInput = () => {
    dispatch(setSearchByAssetType({ assetType: assetsType, search: '' }));
    dispatch(setSearchValue(''));
    searchInputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchByAssetType({ assetType: assetsType, search: e.target.value }));
    dispatch(setSearchValue(e.target.value));
  };

  const searchElement = (
    <BaseInput
      ref={searchInputRef}
      placeholder="Search asset name..."
      value={searchAsset}
      onChange={handleInputChange}
      aria-label="Search input"
      type="text"
      variant="buttonWrapper"
      rightButton={{
        element: searchAsset?.length > 0 ? <Icon icon={CloseIcon} /> : <Icon icon={SearchIcon} />,
        onClick: handleClearInput,
      }}
    />
  );

  return (
    <div className="h-full">
      {enableSearch && (renderSearchInto ? render(searchElement) : searchElement)}

      <div className="flex flex-col items-center justify-center  px-6 py-10 text-center  rounded-lg bg-gray-100  p-10 shadow-sm w-full h-full border border-gray-200 ">
        <h2 className="text-2xl font-semibold text-gray-900  mb-2">Coming Soon</h2>
        <p className="text-gray-700 max-w-sm">This feature is not available yet, but we’re working on it.</p>
      </div>
    </div>
  );
}
